import { Form } from '@/components/ui/form/form';
import { useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { CurrencyField } from '../ui/field/currency-field';
import { SimpleFinAccountField } from '../ui/field/simple-fin-account-field';
import { TextAreaField } from '../ui/field/text-area-field';
import { TextField } from '../ui/field/text-field';
import { Spinner } from '../ui/spinner';
import {
  Account,
  getSimpleFinAccountOptions,
  useAccountForm,
  useGetSimpleFinAccountsApi,
  useGetUserApi,
} from '@liquidbudget/common';
import { AccountTypeSelectField } from '@/components/account-form/account-type-select';

type Props = {
  account?: Account;
  onSuccess: () => void;
};

export function AccountAddForm({ account, onSuccess }: Props) {
  const { data: simpleFinAccounts, isLoading: isSimpleFinAccountsLoading } = useGetSimpleFinAccountsApi();
  const { data: user, isLoading: isUserLoading } = useGetUserApi();
  const { form, onSubmit, isSubmitting, offBudgetGroupPlaceholder } = useAccountForm({ account, onSuccess });

  const selectedId = form.watch('simpleFinAccountId');
  const isArchived = account?.state === 'ARCHIVED';

  // map SimpleFin accounts into select options
  const simpleFinGroups = useMemo(() => {
    return !simpleFinAccounts ? [] : getSimpleFinAccountOptions(simpleFinAccounts);
  }, [simpleFinAccounts]);

  // if a SimpleFin account is chosen, automatically set the form's "currentBalance"
  useEffect(() => {
    if (selectedId && simpleFinAccounts) {
      const linked = simpleFinAccounts.find((acc) => acc.id === selectedId);
      if (linked?.balance) {
        form.setValue('balance', parseFloat(linked.balance));
      }
    }
  }, [selectedId, simpleFinAccounts, form]);

  // if we are still loading return a spinner
  if (!simpleFinAccounts || isSimpleFinAccountsLoading || !user || isUserLoading) {
    return (
      <div className="flex size-full items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-8 mb-2">
        <div className="grid gap-4">
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-8">
              <TextField form={form} name="name" label="Account Name" disabled={isArchived} required />
            </div>
            <div className="col-span-4">
              <AccountTypeSelectField form={form} name="type" label="Type" disabled={isArchived} />
            </div>
          </div>

          <TextField
            form={form}
            name="offBudgetGroup"
            label="Account Group"
            placeholder={offBudgetGroupPlaceholder}
            disabled={isArchived}
          />

          {user.simpleFinEnabled && (
            <SimpleFinAccountField
              control={form.control}
              name="simpleFinAccountId"
              groups={simpleFinGroups}
              disabled={isArchived}
            />
          )}

          {!account && (
            <CurrencyField
              control={form.control}
              allowNegativeValue={true}
              name="balance"
              label="Current Balance"
              disabled={isArchived}
              showAdjustment={true}
              description="This is your current account balance"
            />
          )}

          <TextAreaField
            form={form}
            name="notes"
            label="Notes"
            placeholder="Optional information about your account..."
            disabled={isArchived}
          />
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button type="submit" isLoading={isSubmitting}>
            {account ? 'Update Account' : 'Add Account'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
